/**
 * El área de inversionistas: la puerta y el panel.
 *
 * Es lo que sobrevive de un diccionario de 750 líneas que servía a siete
 * pantallas —«mi espacio», el panel de administración, el portal de marcas, el
 * estudio MICE— y que se fue con el retiro del backend el 2026-09-18. Vuelven
 * dos pantallas, así que vuelven las claves de dos pantallas. Traer las 750
 * habría sido arrastrar seis productos muertos.
 *
 * Qué se recortó y por qué está en `INVENTARIO-INVERSIONISTAS.md` §2.1.
 */

export const area = {
  comun: {
    cargando: 'Cargando…',
    reintentar: 'Volver a intentar',
  },

  /**
   * El mosaico de México. Vive aquí y no dentro de `inversionistas` porque el
   * componente es genérico: dibuja conteos por estado y no sabe de qué panel
   * cuelga.
   */
  mapa: {
    descripcion:
      'Mapa de casillas de México: cada estado es un cuadro, encendido si hay registros.',
    estadoConRegistros: (nombre: string, n: number) =>
      `${nombre}: ${n} ${n === 1 ? 'registro' : 'registros'}`,
    estadoSinRegistros: (clave: string) => `${clave}: sin registros todavía`,
    leyenda: {
      sinSenal: 'Sin señal',
      uno: '1 registro',
      dos: '2 registros',
      tresOmas: '3 o más',
    },
  },

  /**
   * La puerta.
   *
   * **Reescrita el 2026-09-20, no recuperada.** La versión anterior describía un
   * alta de autoservicio con lista de permitidos y cola de aprobación: «si tu
   * correo o el dominio de tu fondo ya están autorizados, entras al momento; si
   * no, lo revisamos y te avisamos». Nada de eso existe ya.
   *
   * El motivo no es de producto: SES está en sandbox en las cuentas donde vive
   * el backend, y en sandbox un correo a un destinatario cualquiera **se acepta
   * y nunca llega** — peor que fallar de frente. Así que las cuentas se crean a
   * mano y aquí no se promete ningún correo. Decidido por Alan el 2026-09-20.
   */
  acceso: {
    titulo: 'Acceso para inversionistas',
    subtitulo:
      'Publicamos nuestra tracción —volumen de interesados, conversión a demo y su evolución mes a mes— para quien esté evaluando invertir.',

    /** El acceso es por invitación, así que se dice antes de pedir nada. */
    porInvitacion:
      'El acceso es por invitación. Si el equipo te dio una cuenta, entra con ella; si no, escríbenos y la preparamos.',

    /*
      El aviso de que el login es de mentira, con copy propio.

      Aquí se reusaba `t.demo.aviso`, que es el que lleva `/demo`. No sirve, y
      el motivo es de fondo y no de parámetro: su cola dice «no corresponden a
      un evento real», que es cierto de unas trayectorias inventadas y falso de
      un formulario de acceso. Un aviso que dice algo que no es deja de avisar.
    */
    deMentiraAntes: 'Este acceso es ',
    deMentiraFuerte: 'una simulación',
    deMentiraDespues:
      ': no valida nada contra ningún servidor y las cuentas están escritas en el propio sitio. No escribas aquí una contraseña que uses en otro lado.',

    correo: 'Correo',
    correoRequerido: 'Escribe el correo con el que te dimos la cuenta.',
    contrasena: 'Contraseña',
    mostrarContrasena: 'Mostrar contraseña',
    ocultarContrasena: 'Ocultar contraseña',
    olvidaste: '¿Olvidaste tu contraseña?',
    contrasenaRequerida: 'Escribe tu contraseña.',
    entrar: 'Entrar',
    entrando: 'Entrando…',
    escribirnos: 'Escribirnos',
    cerrarSesion: 'Cerrar sesión',

    /** Lo que se ve dentro, dicho antes de entrar. */
    avisoAntes: 'Verás ',
    avisoFuerte: 'solo cifras agregadas',
    avisoDespues:
      ': cuántas personas se interesaron, cuántas pidieron demo y cómo evoluciona mes a mes. Los datos personales de quienes se registran no salen del sistema.',

    /** Con sesión, antes de pasar al panel. */
    yaEntraste: (nombre: string) => `Entraste como ${nombre}.`,
    verTraccion: 'Ver la tracción',
  },

  /**
   * El panel.
   *
   * Conteos por estado, **nunca** nombres de organización: la diferencia entre
   * lo que ve un administrador y lo que ve un inversionista se decide en el
   * servidor, no aquí. Filtrar en el navegador no es filtrar.
   */
  inversionistas: {
    titulo: 'Tracción',
    subtitulo: 'solo cifras agregadas',
    cargando: 'Cargando métricas…',
    sinDatos: 'Todavía no hay datos suficientes.',
    falloCargar: 'No se pudieron cargar las métricas.',
    actualizado: (cuando: string) =>
      `Interés registrado en la plataforma. Actualizado al ${cuando}.`,
    avisoAgregadoAntes: 'Este panel muestra ',
    avisoAgregadoFuerte: 'solo cifras agregadas',
    avisoAgregadoDespues:
      '. Los datos personales de quienes se registran no salen del sistema: la agregación ocurre en el servidor, así que ni siquiera viajan hasta este navegador.',

    cobertura: {
      titulo: 'Dónde ya hay señal',
      subtitulo:
        'Contactos capturados en campo con recintos, hoteles y oficinas de convenciones.',
      falloCargar: 'No se pudo cargar la cobertura.',
      kpis: {
        registros: 'Contactos',
        organizaciones: 'Organizaciones',
        estados: 'Estados con señal',
        internacionales: 'Fuera de México',
        pendientes: 'Tarjetas por transcribir',
      },
      porTipo: 'Por tipo de organización',
      nota: 'Se publican conteos por estado. Las organizaciones concretas no se listan.',
      sinCobertura: 'No se pudo leer la cobertura territorial en este momento.',
      cargando: 'Cargando cobertura…',
      /*
       * El estado vacío del mosaico, que es el que está en vigor hoy.
       *
       * Mismo criterio que `embudo.sinDatos`: la única fuente real de cobertura
       * que existe lleva nombres de organización dentro, y lo que viaja en el
       * bundle lo lee quien insista. Así que el mapa se dibuja y no afirma nada
       * hasta que las cifras salgan del servidor contra un token.
       */
      todaviaNo:
        'El levantamiento en campo está en curso. Los conteos por estado aparecerán aquí cuando el panel los lea del servidor.',
    },

    tarjetas: {
      solicitudesDeContacto: 'Solicitudes de contacto',
      cuentasCreadas: 'Cuentas creadas',
      pidieronDemo: 'Pidieron demo',
      enEspera: 'En espera',
      demosAtendidas: 'Demos atendidas',
      conversionADemo: 'Conversión a demo',
      conversionPista: 'de quienes crean cuenta',
    },

    embudo: {
      titulo: 'Embudo del sitio',
      /*
       * El texto que sustituye a seis ceros mientras el formulario web y las
       * cuentas no registren nada. Un cero afirma que se midio; esto dice lo
       * que de verdad pasa.
       */
      sinDatos:
        'El formulario de contacto y el alta de cuentas todavía no registran actividad. Las cifras aparecerán aquí conforme llegue.',
    },

    distribuciones: {
      porIndustria: 'Por industria',
      porTamano: 'Por tamaño de empresa',
      porTipoDeSolicitud: 'Por tipo de solicitud',
      porPerfil: 'Por perfil declarado',
    },

    demo: {
      titulo: 'Ver el producto funcionando',
      descripcion:
        'Una demostración de cómo los sensores miden un stand: trayectorias, aforo por zona y captura de leads con gafete.',
      cta: 'Abrir la demostración',
    },

    evolucionMensual: 'Evolución mensual',
    series: {
      cuentasPorMes: 'Cuentas creadas por mes',
      solicitudesPorMes: 'Solicitudes por mes',
    },
  },

  /**
   * Los errores del puerto de identidad, por código.
   *
   * El adaptador no puede usar un hook —no es un componente— así que devuelve
   * un **código** y lo traduce quien lo enseña. Es el patrón que
   * `sinTextoEnDuro.test.ts` describe en su cabecera, y la razón de que aquí
   * haya una tabla en vez de cadenas repartidas por el código.
   */
  /**
   * La pantalla de administración: quién tiene acceso, y darlo.
   *
   * Vive aquí y no dentro de `inversionistas` porque no es del panel: es otra
   * pantalla, con otro público --una sola persona-- y otra pregunta.
   */
  portero: {
    titulo: 'Quién tiene acceso',
    subtitulo: 'El área de inversionistas es por invitación: no hay registro abierto.',
    aviso:
      'Esta pantalla la sirve el servidor sólo a cuentas con rol de administrador. Que el enlace no se pinte para los demás es una cortesía, no la protección.',
    invitar: {
      titulo: 'Invitar a alguien',
      descripcion:
        'Se crea la cuenta y se sortea una contraseña. Quien entre con ella tendrá que cambiarla.',
      correo: 'Correo',
      nombre: 'Nombre (opcional)',
      organizacion: 'Organización (opcional)',
      cta: 'Crear la cuenta',
      enviando: 'Creando…',
      creada: (correo: string) => `Cuenta creada para ${correo}.`,
      unaSolaVez:
        'Copia la contraseña ahora: se guarda cifrada y no se puede volver a consultar. Mándasela por un canal aparte del correo de la invitación.',
    },
    lista: {
      titulo: 'Cuentas dadas de alta',
      vacia: 'Todavía no hay ninguna cuenta.',
      correo: 'Correo',
      nombre: 'Nombre',
      rol: 'Rol',
      alta: 'Alta',
      ultimoIngreso: 'Último ingreso',
      estado: 'Estado',
      sinNombre: 'Sin nombre',
      nunca: 'Nunca ha entrado',
      activa: 'Activa',
      sinEstrenar: 'Sin estrenar',
      revocada: 'Revocada',
    },
    sucesos: {
      titulo: 'Lo que ha pasado',
      explicacion:
        'La pantalla de recuperar contraseña no puede decir si un correo salió —contestar distinto delataría qué correos existen—, así que el fallo aparece aquí. Hoy el envío se rechaza salvo a direcciones verificadas: SES está en modo de pruebas.',
      cuando: 'Cuándo',
      que: 'Qué',
      detalle: 'Detalle',
    },
    volver: 'Volver al panel',
  },

  /**
   * Recuperar la contraseña. El copy va en CONDICIONAL a propósito.
   *
   * El servidor contesta lo mismo exista o no la cuenta —es lo que impide
   * enumerar correos— así que la pantalla no puede afirmar que el correo salió.
   * Y hay una razón de más: las cuentas están en el sandbox de SES, que entrega
   * sólo a direcciones verificadas, así que hoy el caso normal es que NO llegue.
   */
  recuperar: {
    titulo: 'Cambiar tu contraseña',
    subtitulo: 'Te mandamos un código al correo con el que entras y lo cambias aquí mismo.',
    volver: 'Volver a entrar',
    listo: 'Contraseña cambiada. Ya puedes entrar con la nueva.',
    entrar: 'Ir a entrar',
    paso1: {
      explicacion:
        'Escribe el correo de tu cuenta. Si está dada de alta, te llega un código que sirve una sola vez y caduca en 15 minutos.',
      cta: 'Mandarme el código',
      enviando: 'Mandando…',
    },
    paso2: {
      avisoAntes: 'Si ',
      avisoDespues:
        ' está dado de alta, el código va en camino. Puede tardar un par de minutos; si no llega, escríbenos.',
      codigo: 'Código',
      ejemplo: 'ABCDE-FGHJK',
      nueva: 'Contraseña nueva',
      minimo: 'Al menos 12 caracteres. Larga vale más que complicada.',
      cta: 'Cambiar la contraseña',
      enviando: 'Cambiando…',
      otroCodigo: 'Pedir otro código',
    },
  },

  errores: {
    credenciales: 'El correo o la contraseña no coinciden.',
    red: 'No se pudo contactar el servidor. Revisa tu conexión y vuelve a intentar.',
    sinConfigurar: 'El acceso todavía no está configurado en este entorno.',
    servidor: 'El servidor respondió con un error. Vuelve a intentar en un momento.',
    sesionExpirada: 'Tu sesión expiró. Vuelve a entrar.',
    sinPermiso: 'Tu cuenta no tiene acceso a esta sección. Pide que te la habiliten.',
    codigoInvalido: 'Ese código no es válido. Revísalo o pide uno nuevo.',
    codigoCaducado: 'El código caducó. Pide uno nuevo.',
    contrasenaCorta: 'La contraseña nueva es demasiado corta.',
    generico: 'No se pudo completar la operación.',
  },
};
